import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { RemarkToCodeMapping, ServiceType } from '@industronics/fids-utils';
import { handleGateChange, toCamelCase } from '../utils/common.utils';
import { FlightDetails, FlightStatus, Rules, Template, TemplateColumn } from './interfaces/template.interface';
import { arrivalFlights, flights } from '../data/flights.data';
import "./ListTemplate.css";
import { TripType } from '@industronics/fids-utils';
import { Station } from './interfaces/flight.details';

interface DepartureComponentProps {
    template: Template;
    onItemClick: (rowIndex: number, isHeader: boolean, col: TemplateColumn, field: string, styles: any) => void;
    onBodyRowClick: (index: number) => void;
    onCellClick: (col: TemplateColumn) => void;
    updateTemplate: (template: Template) => void;
    onRemarkCellClick: (styles: any, key: string) => void;
    onPageClick?: () => void;
    animationDirection: string;
    flipSpeed: string;
}

const DepartureComponent: React.FC<DepartureComponentProps> = ({ template, onPageClick, onRemarkCellClick, onItemClick, onBodyRowClick, onCellClick, updateTemplate, animationDirection, flipSpeed }) => {


    const [selectedHeaderField, setSelectedHeaderField] = useState<string | null>(null);
    const [selectedBodyField, setSelectedBodyField] = useState<string | null>(null);
    const [selectedBodyRowIndex, setSelectedBodyRowIndex] = useState<number | null>(null);
    const [selectedCell, setSelectedCell] = useState<{ field: string; rowIndex: number | null } | null>(null);
    const [selectedRemarkKey, setSelectedRemarkKey] = useState<string | null>(null);

    const [hoverStyle, setHoverStyle] = useState({});
    const [clickedStyle, setClickedStyle] = useState<string | null>(null);

    const [codeShareFlightIndices, setCodeShareFlightIndices] = useState<{ [key: string]: number }>({});
    const [stationIndices, setStationIndices] = useState<{ [key: string]: number }>({});
    const timezone = process.env.REACT_APP_TIMEZONE || 8;
    const [animatedFlightIds, setAnimatedFlightIds] = useState<{ [key: string]: boolean }>({}); // Track which flights are animating
    const rules: Rules[] = [
        {
            "condition": "Before",
            "hours": 2,
            "remarkCode": {
                "key": "GSH",
                "description": "Gate Shown"
            },
        },
        {
            "condition": "Before",
            "hours": 1,
            "remarkCode": {
                "key": "GTG",
                "description": "Go to Gate"
            },
        }
    ]

    //pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [isFading, setIsFading] = useState(false); // Track fading effect

    // Automatic page flipping
    const rowsPerPage = 12;
    const totalPages = Math.ceil(flights.length / rowsPerPage);
    const currentFlights: FlightDetails[] = (template.tripType === TripType.Departure ? flights : arrivalFlights).slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    const longestFlightNumberLength = currentFlights?.reduce(
        (max, current) => (current.flightNumber.length > max ? current.flightNumber.length : max),
        0
    );


    useEffect(() => {
        // Apply fade-out and fade-in effect on page flip
        if (totalPages === 1) {
            setCurrentPage(1);
            return;
        };
        const interval = setInterval(() => {
            setIsFading(true); // Trigger fade-out
            setTimeout(() => {
                setCurrentPage((prevPage) => (prevPage < totalPages ? prevPage + 1 : 1));
                setIsFading(false); // Reset to fade-in
            }, 500); // Duration should match CSS transition
        }, currentPage === 1 ? 10 * 1000 : 5 * 1000);

        return () => clearInterval(interval);
    }, [currentPage, totalPages]);

    // Navigate between pages
    const goToPreviousPage = () => {
        setIsFading(true); // Trigger fade-out
        setTimeout(() => {
            setCurrentPage((prevPage) => (prevPage > 1 ? prevPage - 1 : Math.ceil(flights.length / rowsPerPage)));
            setIsFading(false); // Reset to fade-in
        }, 500); // Duration should match CSS transition
    };

    const goToNextPage = () => {
        setIsFading(true); // Trigger fade-out
        setTimeout(() => {
            setCurrentPage((prevPage) => (prevPage < Math.ceil(flights.length / rowsPerPage) ? prevPage + 1 : 1));
            setIsFading(false); // Reset to fade-in
        }, 500); // Duration should match CSS transition
    };

    useEffect(() => {
        const intervalId = setInterval(() => {
            setCodeShareFlightIndices((prevIndices) => {
                const newIndices = { ...prevIndices };
                flights.forEach((flight) => {
                    if (flight.codeShareFlight && flight.codeShareFlight.length > 0) {
                        const currentIndex = prevIndices[flight._id] || 0;
                        newIndices[flight._id] = (currentIndex + 1) % flight.codeShareFlight.length;
                    }
                });
                return newIndices;
            });
        }, 3000); // 3-second interval

        return () => {
            clearInterval(intervalId);
        };
    }, [flights]);

    // Update stationIndices for flight stations
    useEffect(() => {
        const stationIntervalId = setInterval(() => {
            setStationIndices((prevIndices) => {
                const newIndices = { ...prevIndices };
                flights.forEach((flight) => {
                    if (flight.stations && flight.stations.length > 1) {
                        const currentIndex = prevIndices[flight._id] || 0;
                        newIndices[flight._id] = (currentIndex + 1) % (flight.stations.length - 1); // Exclude the final destination
                    }
                });
                return newIndices;
            });
        }, 3000);

        return () => {
            clearInterval(stationIntervalId);
        };
    }, [flights]);


    const checkRule = (flight: FlightDetails) => {
        const now = moment();
        const flightETA = moment(new Date(flight?.ETA));
        if (
            flight?.remarkCode?.key === RemarkToCodeMapping.GATE_OPEN
            || flight?.remarkCode?.key === RemarkToCodeMapping.GATE_CLOSED
            || flight?.remarkCode?.key === RemarkToCodeMapping.CANCELLED
            || flight?.remarkCode?.key === RemarkToCodeMapping.FINAL_CALL
            || flight?.remarkCode?.key === RemarkToCodeMapping.DEPARTED
            || now.isAfter(flightETA, 'minute')
        ) return null;
        return true;
    }

    const getRuleRemark = (flight: FlightDetails): FlightStatus | undefined | null => {
        const now = moment();
        const flightETA = moment(new Date(flight?.ETA));

        const gateShownRule = rules.find(rule => rule?.remarkCode?.key === RemarkToCodeMapping.GATE_SHOWN); // Gate Shown
        const goToGateRule = rules.find(rule => rule?.remarkCode?.key === RemarkToCodeMapping.GO_TO_GATE); // Go to Gate

        if (!gateShownRule || !goToGateRule) return null;

        const gateShownTime = flightETA.clone().subtract(gateShownRule.hours * 60, 'minutes');
        const goToGateTime = flightETA.clone().subtract(goToGateRule.hours * 60, 'minutes');

        if (now.isBetween(gateShownTime, goToGateTime)) {
            return { key: RemarkToCodeMapping.GATE_SHOWN, description: `${gateShownRule?.remarkCode?.description} ${gateShownRule?.remarkCode?.key === RemarkToCodeMapping.GATE_SHOWN ? " at " + gateShownTime.format('HH:mm') : ""}` };
        } else if (now.isSameOrAfter(goToGateTime)) {
            if (flight.standCodeChanged) return { key: RemarkToCodeMapping.GATE_CHANGE, description: "Gate Change" };
            return { key: RemarkToCodeMapping.GO_TO_GATE, description: `${goToGateRule?.remarkCode?.description} ${goToGateRule?.remarkCode?.key === RemarkToCodeMapping.GATE_SHOWN ? " at " + goToGateTime.format('HH:mm') : ""}` };
        }

        return null;
    };

    const handleOnHeaderItemClick = (col: TemplateColumn) => {
        onItemClick(-1, true, col, col.field, col.header?.inLineStyle || []);
        setSelectedHeaderField(col.field);
        setSelectedBodyRowIndex(null);
        setSelectedRemarkKey(null);
        setHoverStyle({});
        setClickedStyle(null);
    }

    const handleOnBodyItemClick = (index: number) => {
        setSelectedBodyRowIndex(index);
        setSelectedHeaderField(null);
        onBodyRowClick(index);
        setHoverStyle({});
        setClickedStyle(null);
        setSelectedRemarkKey(null);

    }


    const [draggedColumnIndex, setDraggedColumnIndex] = useState<number | null>(null);

    // Handle drag start
    const handleDragStart = (index: number) => {
        setDraggedColumnIndex(index);
    };

    // Handle drag over
    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault(); // Allow dropping
    };

    // Handle drop
    const handleDrop = (droppedColumnIndex: number) => {
        if (draggedColumnIndex === null || draggedColumnIndex === droppedColumnIndex) return;

        // Swap columns in the template
        const updatedColumns = [...template?.draft!.columns];
        const [draggedColumn] = updatedColumns.splice(draggedColumnIndex, 1);
        updatedColumns.splice(droppedColumnIndex, 0, draggedColumn);

        // Update template with swapped columns
        const updatedTemplate = {
            ...template,
            draft: { ...template.draft!, columns: updatedColumns },
        };
        updateTemplate(updatedTemplate)

        setDraggedColumnIndex(null); // Reset drag state
    };

    // Handles Flip Animation Speed
    const getPerItemDuration = (flipSpeed: string): number => {
        return flipSpeed === "slow" ? 3 : flipSpeed === "fast" ? 1 : 2;
    };

    return (
        <div
            onClick={(event) => {
                event.stopPropagation();
                setSelectedBodyRowIndex(null);
                setSelectedHeaderField(null);
                setSelectedBodyField(null);
                setHoverStyle({});
                setClickedStyle(null);
                setSelectedRemarkKey(null);
                setSelectedCell(null);

            }}
            style={{ width: "100%", fontSize: template?.draft?.pageFontSize, }}
            className="min-h-screen bg-gray-800 text-white p-2">

            <div
                className="table-container">
                <table style={{ width: "100%", fontSize: template?.draft?.pageFontSize }}
                    className={`w-full text-center ${isFading ? 'shimmer' : ''}`}
                >
                    <thead className="bg-gray-700">
                        <tr style={{ border: "1px solid" }}>

                            {template?.draft?.columns?.map((col, index) => (
                                <th
                                    key={col.field}
                                    draggable // Enable drag
                                    onDragStart={() => handleDragStart(index)} // Track dragged column
                                    onDragOver={handleDragOver} // Allow drag over
                                    onDrop={() => handleDrop(index)} // Handle drop event
                                    className={(draggedColumnIndex === index || selectedHeaderField === col.field ? "selected-item " : " ") + col.header?.cssClass || ""}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        handleOnHeaderItemClick(col);
                                    }}
                                    style={{
                                        fontSize: template?.draft?.pageFontSize || "23px",
                                        ...col.header?.inLineStyle?.reduce((styleObj: any, style) => {
                                            const camelCaseProp = toCamelCase(style.prop);
                                            styleObj[camelCaseProp] = style.value;
                                            return styleObj;
                                        }, {}),
                                    }}
                                >
                                    {col.header?.label || ""}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {currentFlights.map((flight: any, index) => (
                            <tr style={{
                                backgroundColor: index % 2 == 0 ? template?.draft?.alternateRow?.background : "initial",
                                color: index % 2 == 0 ? template?.draft?.alternateRow?.color : "initial",

                            }} key={index}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleOnBodyItemClick(index);
                                }}
                                className={(selectedBodyRowIndex === index ? "selected-item " : " ") + "odd:bg-gray-600 even:bg-gray-700"}
                            >

                                {template?.draft?.columns?.map((col: any) => {
                                    let cellContent;

                                    switch (col.field) {
                                        case "ETA":
                                        case "counterOpeningTime":
                                        case "counterClosingTime":
                                        case "loungeOpeningTime":
                                        case "loungeClosingTime":
                                        case "loungeBoardingTime":
                                        case "finalLoungeTime":
                                        case "lastBagReceivedAt":
                                        case "scheduledTravelTime":
                                        case "officialFlightDate":
                                            cellContent = (
                                                <span
                                                    style={(col.body?.inLineStyle || []).reduce((styleObj: any, style: any) => {
                                                        const camelCaseProp = toCamelCase(style.prop);
                                                        if (camelCaseProp !== "backgroundColor") styleObj[camelCaseProp] = style.value;
                                                        if (camelCaseProp === "color") styleObj[camelCaseProp] = index % 2 == 0 ? template?.draft?.alternateRow?.color : style.value;
                                                        return styleObj;
                                                    }, {})}
                                                >
                                                    <span style={{ display: "block", lineHeight: 1.2 }}> {moment(flight[col.field]).utcOffset(timezone).format(col.body?.time?.format || "HH:mm")} </span>
                                                    <small style={{ display: "block", lineHeight: 1.2, marginTop: "-5px", marginLeft: "5px" }}>
                                                        {
                                                            moment(flight[col.field]).utcOffset(timezone).isSame(moment(), 'day')
                                                                ? ''
                                                                : (
                                                                    <small>
                                                                        <small
                                                                            style={{
                                                                                ...(col.body?.date?.inLineStyle || []).reduce((styleObj: any, style: any) => {
                                                                                    const camelCaseProp = toCamelCase(style.prop);
                                                                                    styleObj[camelCaseProp] = style.value;
                                                                                    return styleObj;
                                                                                }, {}),
                                                                                ...hoverStyle,
                                                                                ...(clickedStyle === col.field ? { border: "2px solid #cfff45" } : {}),
                                                                            }}
                                                                            onMouseEnter={() => setHoverStyle({ border: "1px solid #cfff45" })}
                                                                            onMouseLeave={() => setHoverStyle({})}
                                                                            onClick={(event) => {
                                                                                event.stopPropagation();
                                                                                setClickedStyle(col.field);
                                                                                onCellClick(col);
                                                                                setSelectedBodyRowIndex(-1);
                                                                                setSelectedRemarkKey(null);
                                                                            }}
                                                                        >{moment(flight[col.field]).utcOffset(timezone).format(col.body?.date?.format || "DD MMM")}</small>
                                                                    </small>
                                                                )}
                                                    </small>
                                                </span>
                                            );
                                            break;
                                        case "airline":
                                            cellContent = (
                                                <div>
                                                    <img
                                                        className='flight-logo'
                                                        src={`ak.png`}
                                                        alt={flight.airline?.code}
                                                        onError={(e) => {
                                                            (e.target as HTMLImageElement).style.display = 'none';
                                                        }}
                                                    />
                                                </div>
                                            );
                                            break;

                                        case "flightNumber":
                                            if (!col?.body?.showFlightsInline) {
                                                const perItem = getPerItemDuration(flipSpeed);
                                                const itemCount = flight.codeShareFlight.length + 1;
                                                const totalDuration = `${itemCount * perItem}s`;
                                                cellContent = (
                                                    <div>
                                                        <label
                                                            style={{
                                                                width: template?.draft?.pageFontSize
                                                                    ? (((parseInt(template?.draft?.pageFontSize, 10) - 10) * 4 + (longestFlightNumberLength <= 7 ? 45 : 55)) + "px")
                                                                    : "10px"
                                                            }}
                                                        >
                                                            {flight.flightNumber}
                                                        </label>

                                                        {flight?.codeShareFlight?.length > 0 && <span> | </span>}

                                                        {flight?.codeShareFlight?.length > 0 && (
                                                            flight?.codeShareFlight?.length > 1 ? (
                                                                <div
                                                                    key={`flightNumber-${flight._id}-${animationDirection}`}
                                                                    className="text-flip"
                                                                    style={{
                                                                        height: template?.draft?.pageFontSize
                                                                            ? `${parseInt(template?.draft?.pageFontSize, 10) + 4}px`
                                                                            : "15px",
                                                                        '--items': flight.codeShareFlight.length + (flight?.codeShareFlight?.length > 1 ? 1 : 0),
                                                                        '--animation-duration': totalDuration,
                                                                    } as React.CSSProperties}
                                                                >
                                                                    <div
                                                                        className="text-container"
                                                                        style={{
                                                                            animationName: animationDirection === "down" ? "flipDown" : "flipUp",
                                                                            animationDuration: `var(--animation-duration)`,
                                                                            animationTimingFunction: "linear",
                                                                            animationIterationCount: "infinite",
                                                                        }}
                                                                    >
                                                                        {flight.codeShareFlight.map((item: string, idx: number) => (
                                                                            <div key={item}>
                                                                                <label key={idx}>{item}</label>
                                                                            </div>
                                                                        ))}
                                                                        <div>
                                                                            <label>{flight.codeShareFlight[0]}</label>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <span>{flight.codeShareFlight[0]}</span>
                                                            )
                                                        )}
                                                    </div>
                                                );
                                            } else {
                                                const updatedCodeShareFlight = [
                                                    flight.flightNumber,
                                                    ...(flight?.codeShareFlight || []),
                                                ];
                                                const perItem = getPerItemDuration(flipSpeed);
                                                const itemCount = updatedCodeShareFlight.length;
                                                const totalDuration = `${itemCount * perItem}s`;
                                                cellContent = (
                                                    <div>
                                                        <div
                                                            key={`flightNumber-${flight._id}-${animationDirection}`}
                                                            className="text-flip"
                                                            style={{
                                                                '--items': updatedCodeShareFlight.length + (updatedCodeShareFlight.length > 1 ? 1 : 0),
                                                                '--animation-duration': totalDuration,
                                                            } as React.CSSProperties}
                                                        >
                                                            <div
                                                                className="text-container"
                                                                style={{
                                                                    animationName: animationDirection === "down" ? "flipDown" : "flipUp",
                                                                    animationDuration: `var(--animation-duration)`,
                                                                    animationTimingFunction: "linear",
                                                                    animationIterationCount: "infinite",
                                                                }}
                                                            >
                                                                {updatedCodeShareFlight.length > 1 && updatedCodeShareFlight.map((item: string, idx: number) => (
                                                                    <div key={item}>
                                                                        <label className={item === flight.flightNumber ? 'main-flight' : ""}>
                                                                            {item}
                                                                        </label>
                                                                    </div>
                                                                ))}
                                                                <div>
                                                                    <label
                                                                        className={
                                                                            updatedCodeShareFlight.length > 1 &&
                                                                                updatedCodeShareFlight[0] === flight.flightNumber
                                                                                ? 'main-flight' : ""
                                                                        }
                                                                    >
                                                                        {updatedCodeShareFlight[0]}
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            break;
                                        // case "flightNumber":
                                        //     if (!col?.body?.showFlightsInline) {
                                        //         const perItem = getPerItemDuration(flipSpeed);
                                        //         const itemCount = flight.codeShareFlight.length + 1;
                                        //         const totalDuration = `${itemCount * perItem}s`;
                                        //         cellContent = (<div>
                                        //             {col?.body?.showLogo && <img
                                        //                 className='flight-logo'
                                        //                 src='ak.png' />}
                                        //             <label style={{ width: template?.draft?.pageFontSize ? (((parseInt(template?.draft?.pageFontSize, 10) - 10) * 4 + (longestFlightNumberLength <= 7 ? 45 : 55)) + "px") : "10px" }}>
                                        //                 {flight.flightNumber}
                                        //             </label>
                                        //             {flight?.codeShareFlight?.length > 0 && <span> | </span>}
                                        //             {flight?.codeShareFlight?.length > 0 && (
                                        //                 flight?.codeShareFlight?.length > 1 ? (
                                        //                     <div
                                        //                         key={`flightNumber-${flight._id}-${animationDirection}`}
                                        //                         className={"text-flip"}
                                        //                         style={{
                                        //                             height: template?.draft?.pageFontSize
                                        //                                 ? `${parseInt(template?.draft?.pageFontSize, 10) + 4}px`
                                        //                                 : "15px",
                                        //                             '--items': flight.codeShareFlight.length + (flight?.codeShareFlight?.length > 1 ? 1 : 0),
                                        //                             '--animation-duration': totalDuration,
                                        //                         } as React.CSSProperties}
                                        //                     >
                                        //                         <div className="text-container"
                                        //                             style={{
                                        //                                 animationName: animationDirection === "down" ? "flipDown" : "flipUp",
                                        //                                 animationDuration: `var(--animation-duration)`,
                                        //                                 animationTimingFunction: "linear",
                                        //                                 animationIterationCount: "infinite",
                                        //                             }}>
                                        //                             {flight.codeShareFlight.map((item: string, index: number) => (
                                        //                                 <div key={item}>
                                        //                                     {col?.body?.showLogo && (
                                        //                                         <img
                                        //                                             className='flight-logo'
                                        //                                             src="ak.png"
                                        //                                         />
                                        //                                     )}
                                        //                                     <label key={index}>{item}</label>
                                        //                                 </div>
                                        //                             ))}
                                        //                             <div>
                                        //                                 {col?.body?.showLogo && (
                                        //                                     <img
                                        //                                         className='flight-logo'
                                        //                                         src="ak.png"
                                        //                                     />
                                        //                                 )}
                                        //                                 <label>{flight.codeShareFlight[0]}</label>
                                        //                             </div>
                                        //                         </div>
                                        //                     </div>
                                        //                 ) : (
                                        //                     <span>
                                        //                         {col?.body?.showLogo && (
                                        //                             <img
                                        //                                 className='flight-logo'
                                        //                                 src="ak.png"
                                        //                             />
                                        //                         )}
                                        //                         <span>{flight.codeShareFlight[0]}</span>
                                        //                     </span>
                                        //                 )
                                        //             )}

                                        //         </div>);
                                        //     }
                                        //     else {
                                        //         const updatedCodeShareFlight = [
                                        //             flight.flightNumber,
                                        //             ...(flight?.codeShareFlight || []),
                                        //         ];
                                        //         const perItem = getPerItemDuration(flipSpeed);
                                        //         const itemCount = updatedCodeShareFlight.length;
                                        //         const totalDuration = `${itemCount * perItem}s`;
                                        //         cellContent = (
                                        //             <div>
                                        //                 {(
                                        //                     <div
                                        //                         key={`flightNumber-${flight._id}-${animationDirection}`}
                                        //                         className="text-flip"
                                        //                         style={{
                                        //                             '--items': updatedCodeShareFlight.length + (updatedCodeShareFlight.length > 1 ? 1 : 0), // Add an extra slot for flightNumber
                                        //                             '--animation-duration': totalDuration,
                                        //                         } as React.CSSProperties}
                                        //                     >
                                        //                         <div className="text-container"
                                        //                             style={{
                                        //                                 animationName: animationDirection === "down" ? "flipDown" : "flipUp",
                                        //                                 animationDuration: `var(--animation-duration)`,
                                        //                                 animationTimingFunction: "linear",
                                        //                                 animationIterationCount: "infinite",
                                        //                             }}>
                                        //                             {updatedCodeShareFlight.length > 1 && updatedCodeShareFlight.map((item: string, index: number) => (
                                        //                                 <div key={item}>
                                        //                                     {col?.body?.showLogo && (
                                        //                                         <img
                                        //                                             className='flight-logo'
                                        //                                             src="ak.png"
                                        //                                             alt="Airline Logo"
                                        //                                         />
                                        //                                     )}
                                        //                                     <label className={item === flight.flightNumber ? 'main-flight' : ""}>{item}</label>
                                        //                                 </div>
                                        //                             ))}

                                        //                             <div>
                                        //                                 {col?.body?.showLogo && (
                                        //                                     <img
                                        //                                         className='flight-logo'
                                        //                                         src="ak.png"
                                        //                                         alt="Airline Logo"
                                        //                                     />
                                        //                                 )}
                                        //                                 <label className={updatedCodeShareFlight.length > 1 && updatedCodeShareFlight[0] === flight.flightNumber ? 'main-flight' : ""}>{updatedCodeShareFlight[0]}</label>
                                        //                             </div>


                                        //                         </div>
                                        //                     </div>
                                        //                 )}
                                        //             </div>
                                        //         );

                                        //     }
                                        //     break;
                                        case "stations":
                                            let updatedStations: Station[] = [];
                                            if (template.tripType === TripType.Departure) updatedStations = flight.stations.filter((_: any, index: number) => index !== 0);
                                            else updatedStations = flight.stations.filter((_: any, index: number) => index !== flight.stations.length - 1);

                                            const perItem = getPerItemDuration(flipSpeed);
                                            const stationItemCount = updatedStations.length;
                                            const stationDuration = `${stationItemCount * perItem}s`;

                                            cellContent = (
                                                <div>
                                                    {(
                                                        <div
                                                            key={`stations-${flight._id}-${animationDirection}`}
                                                            className={updatedStations.length > 1 ? "text-flip" : ""}
                                                            style={{
                                                                width: "100%",
                                                                '--items': updatedStations.length + (updatedStations.length > 1 ? 1 : 0), // Add an extra slot for flightNumber
                                                                '--animation-duration': stationDuration,
                                                            } as React.CSSProperties}
                                                        >
                                                            <div className="text-container"
                                                                style={{
                                                                    animationName: animationDirection === "down" ? "flipDown" : "flipUp",
                                                                    animationDuration: `var(--animation-duration)`,
                                                                    animationTimingFunction: "linear",
                                                                    animationIterationCount: "infinite",
                                                                }}>
                                                                {updatedStations.length > 1 && updatedStations.map((item: Station, index: number) => (
                                                                    <div key={item.stationNumber}>
                                                                        <label className={updatedStations.length > 1 && (template.tripType === TripType.Departure ? index + 1 === updatedStations.length : index === 0) ? 'main-flight' : ""}>{item.airport}</label>
                                                                    </div>
                                                                ))}

                                                                <div>
                                                                    <label >{updatedStations[0]?.airport}</label>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>)

                                            break;
                                        case "standCode":
                                            cellContent = (
                                                <span className="p-1"> {(getRuleRemark(flight)?.key === RemarkToCodeMapping.GATE_SHOWN ? "" : handleGateChange(flight.standCode))}</span>
                                            );
                                            break;

                                        case "checkInType":
                                            cellContent = (
                                                <span className="p-1"> {flight?.checkInType?.description}</span>
                                            );
                                            break;

                                        case "loungeCode":
                                            cellContent = (
                                                <span className="p-1"> {handleGateChange(flight.loungeCode)}</span>
                                            );
                                            break;

                                        case "remarkCode":
                                            const specificStyles =
                                                col.body?.specificStyles?.[flight?.remarkCode?.key] || {};

                                            cellContent = (
                                                <span
                                                    className={"p-1"}
                                                    style={(specificStyles?.inLineStyle || []).reduce(
                                                        (styleObj: any, style: any) => {
                                                            const camelCaseProp = toCamelCase(style.prop);
                                                            styleObj[camelCaseProp] = style.value;
                                                            return styleObj;
                                                        },
                                                        {}
                                                    )}
                                                >
                                                    <span className={`remark ${animatedFlightIds[flight._id] ? 'animate-swap' : ''}`}>
                                                        {flight.ETA && checkRule(flight) && flight?.remarkCode?.key !== RemarkToCodeMapping.DELAYED ? "" : flight?.remarkCode?.description}
                                                        {flight.ETA && checkRule(flight) && (
                                                            <span style={{ fontSize: !checkRule(flight) ? "small" : "23px" }} className="text-arrival"> {getRuleRemark(flight)?.description}</span>
                                                        )}
                                                    </span>
                                                </span>
                                            )
                                            break;
                                        default:
                                            cellContent = flight[col.field] || "";
                                    }

                                    return (
                                        <td
                                            key={col.field}
                                            className={(selectedBodyField === col.field && selectedRemarkKey === flight?.remarkCode?.key ? "selected-item " : " ") + col.body?.cssClass || ""}
                                            style={{
                                                fontSize: template?.draft?.pageFontSize || "23px",
                                                ...col.body?.inLineStyle?.reduce((styleObj: any, style: any) => {
                                                    const camelCaseProp = toCamelCase(style.prop);
                                                    if (index % 2 == 0 && camelCaseProp === "color") {
                                                        styleObj[camelCaseProp] = template?.draft?.alternateRow?.color || style.value;
                                                    }
                                                    else if (index % 2 == 0 && camelCaseProp === "backgroundColor") {
                                                        styleObj[camelCaseProp] = template?.draft?.alternateRow?.background || style.value;
                                                    }
                                                    else styleObj[camelCaseProp] = style.value;
                                                    return styleObj;
                                                }, {}),
                                            }}
                                            onClick={(event) => {
                                                if (col.field === 'remarkCode') {
                                                    event.stopPropagation();
                                                    setSelectedBodyRowIndex(null);
                                                    setSelectedHeaderField(null);
                                                    setSelectedBodyField("remarkCode");
                                                    setHoverStyle({});
                                                    setClickedStyle(null);

                                                    setSelectedRemarkKey(flight?.remarkCode?.key);
                                                    onRemarkCellClick(col?.body?.specificStyles[flight?.remarkCode?.key]?.inLineStyle || [], flight?.remarkCode?.key);
                                                }
                                                else {
                                                    setSelectedRemarkKey(null);
                                                    onItemClick(index, false, col, col.field, col.body?.inLineStyle || []);
                                                }
                                            }}
                                        >
                                            {cellContent}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

        </div>
    );
};

export default DepartureComponent;
